<template>
  <v-dialog v-model="modalStore.isNewDataLogModalVisible" persistent max-width="900px">
    <v-card>
      <v-card-title v-if="modalStore.newDataLogModalVerb === 'create'">
        Create A New Data Log
      </v-card-title>

      <v-card-title v-if="modalStore.newDataLogModalVerb === 'edit'">
        Edit Data Log
      </v-card-title>

      <v-card-title v-if="modalStore.newDataLogModalVerb === 'delete'">
        Delete Data Log
      </v-card-title>

      <v-card-text>
        <v-form ref="newDataLogForm" v-if="modalStore.newDataLogModalVerb !== 'delete'">
          <v-row dense>
            <v-col cols="12" v-if="modalStore.newDataLogModalMode === 'admin' || debugMode">
              <v-text-field
                v-model="newDataLog.aquarium_id"
                label="Aquarium ID"
                :rules="[value => !!value || 'Aquarium ID is required']"
                :readonly="modalStore.newDataLogModalMode === 'user'"
                dense
                hide-details="auto"
                required
              />
            </v-col>
            <v-col cols="12">
              <v-text-field
                v-model="newDataLog.timestamp"
                label="Date (YYYY-MM-DD HH:MM AM/PM) (required)"
                :rules="[value => !!value || 'Date is required']"
                dense
                hide-details="auto"
                required
              />
            </v-col>
          </v-row>

          <v-row dense>
            <!-- Physical Conditions -->
            <v-col cols="12" md="4">
              <v-card class="pa-2" outlined>
                <v-card-title class="subtitle-2 py-1 px-2">
                  Physical Conditions
                  <v-tooltip bottom>
                    <template #activator="{ props }">
                      <v-icon
                        x-small
                        class="ml-1 text-h6"
                        color="grey"
                        v-bind="props"
                        style="cursor: pointer;"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      Basic environmental properties of your aquarium water that affect its stability and ability to support life.
                    </span>
                  </v-tooltip>
                </v-card-title>
                <v-card-text class="py-1 px-2">
                  <v-row dense>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.temperature"
                            label="Temperature (°F)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Temperature affects metabolism, oxygen levels, immune response, and comfort of aquatic life including coral health.<br>
                              <strong>Ideal:</strong> 76–80°F (24–27°C)<br>
                              <strong>To Increase:</strong> Use an aquarium heater or increase room temperature.<br>
                              <strong>To Decrease:</strong> Use a chiller, fans, or lower room temperature.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.salinity"
                            label="Salinity (SG)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Salinity (Specific Gravity) is crucial for osmoregulation (osmotic balance) and coral/fish/invert survival.<br>
                              <strong>Ideal:</strong> 1.023–1.026 SG<br>
                              <strong>To Increase:</strong> Top off with saltwater or replace with higher concentrated saltwater (use caution).<br>
                              <strong>To Decrease:</strong> Top off with freshwater (RO/DI).
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.ph"
                            label="pH"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              pH reflects hydrogen ion concentration and overall acidity/alkalinity.<br>
                              <strong>Ideal:</strong> 7.8–8.5 (reef: 8.1–8.4)<br>
                              <strong>To Increase:</strong> Increase aeration, add buffer, or dose kalkwasser.<br>
                              <strong>To Decrease:</strong> Reduce aeration, add CO₂, or use pH-lowering products.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Nutrients & Waste -->
            <v-col cols="12" md="4">
              <v-card class="pa-2" outlined>
                <v-card-title class="subtitle-2 py-1 px-2">
                  Nutrients & Waste
                  <v-tooltip bottom>
                    <template #activator="{ props }">
                      <v-icon
                        x-small
                        class="ml-1 text-h6"
                        color="grey"
                        v-bind="props"
                        style="cursor: pointer;"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      Biological compounds produced by livestock and from feeding; high levels can be harmful and indicate water quality issues.
                    </span>
                  </v-tooltip>
                </v-card-title>
                <v-card-text class="py-1 px-2">
                  <v-row dense>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.ammonia"
                            label="Ammonia (ppm)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Ammonia is the byproduct of fish waste and decomposing matter and is highly toxic to aquatic life. It should be kept as close to zero as possible.<br>
                              <strong>Ideal:</strong> 0 ppm<br>
                              <strong>To Increase:</strong> Not recommended!! Avoid overstocking or overfeeding.<br>
                              <strong>To Decrease:</strong> Perform water changes, improve biological filtration, use ammonia detoxifiers.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.nitrite"
                            label="Nitrite (ppm)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Nitrite is an intermediate compound from the nitrogen cycle and also toxic at elevated levels. Its presence indicates incomplete biological filtration.<br>
                              <strong>Ideal:</strong> 0 ppm<br>
                              <strong>To Increase:</strong> Not recommended!! Avoid overstocking or overfeeding.<br>
                              <strong>To Decrease:</strong> Perform water changes, ensure adequate biological filtration.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.nitrate"
                            label="Nitrate (ppm)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Nitrate is the end product of the nitrogen cycle and is less toxic but can still stress livestock and fuel algae growth at high levels.<br>
                              <strong>Ideal:</strong> &lt;10 ppm (reef), &lt;40 ppm (fish-only)<br>
                              <strong>To Increase:</strong> Not recommended!! avoid overstocking or overfeeding.<br>
                              <strong>To Decrease:</strong> Perform water changes, use nitrate-removing media, add macroalgae or refugium.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.phosphase"
                            label="Phosphate (ppm)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Phosphate is the byproduct of food and waste and encourages algae growth and can inhibit coral calcification.<br>
                              <strong>Ideal:</strong> &lt;0.03 ppm (reef), &lt;0.1 ppm (fish-only)<br>
                              <strong>To Increase:</strong> Feed more or use phosphate additives (rarely needed).<br>
                              <strong>To Decrease:</strong> Use phosphate removers (GFO, resins), perform water changes, reduce feeding.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Water Chemistry -->
            <v-col cols="12" md="4">
              <v-card class="pa-2" outlined>
                <v-card-title class="subtitle-2 py-1 px-2">
                  Water Chemistry
                  <v-tooltip bottom>
                    <template #activator="{ props }">
                      <v-icon
                        x-small
                        class="ml-1 text-h6"
                        color="grey"
                        v-bind="props"
                        style="cursor: pointer;"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span>
                      Key minerals and buffering capacity that are critical for coral growth, shell formation, and overall stability.
                    </span>
                  </v-tooltip>
                </v-card-title>
                <v-card-text class="py-1 px-2">
                  <v-row dense>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.calcium"
                            label="Calcium (ppm)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Calcium is essential for the growth of coral skeletons and invertebrate shells.<br>
                              <strong>Ideal:</strong> 380–450 ppm<br>
                              <strong>To Increase:</strong> Dose calcium supplements (chloride, kalkwasser, two-part).<br>
                              <strong>To Decrease:</strong> Stop dosing, perform water changes with lower-calcium salt mix.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.alkalinity"
                            label="Alkalinity (dKH)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Alkalinity buffers pH (stops it from changing too rapidly) and supports coral calcification.<br>
                              <strong>Ideal:</strong> 7–11 dKH<br>
                              <strong>To Increase:</strong> Dose alkalinity buffers (bicarbonate, carbonate, two-part).<br>
                              <strong>To Decrease:</strong> Stop dosing, perform water changes with lower-alkalinity salt mix.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters align="center">
                        <v-col>
                          <v-text-field
                            v-model="newDataLog.magnesium"
                            label="Magnesium (ppm)"
                            type="number"
                            dense
                            hide-details="auto"
                          />
                        </v-col>
                        <v-col cols="auto">
                          <v-tooltip bottom>
                            <template #activator="{ props }">
                              <v-icon
                                x-small
                                class="ml-1"
                                color="grey"
                                v-bind="props"
                                style="cursor: pointer;"
                              >
                                mdi-information
                              </v-icon>
                            </template>
                            <span>
                              Magnesium helps stabilize calcium and alkalinity levels and is essential for coral health.<br>
                              <strong>Ideal:</strong> 1250–1350 ppm<br>
                              <strong>To Increase:</strong> Dose magnesium supplements.<br>
                              <strong>To Decrease:</strong> Stop dosing, perform water changes with lower-magnesium salt mix.
                            </span>
                          </v-tooltip>
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-textarea
            v-model="newDataLog.note"
            label="Notes"
            dense
            hide-details="auto"
            rows="2"
            auto-grow
          />
        </v-form>
        <p v-if="modalStore.newDataLogModalVerb === 'delete'">
          Are you sure you want to delete this data log?
        </p>
      </v-card-text>

      <v-card-actions>
        <span class="text-subtle mr-2" style="align-self: center;">
          The date is the only required input field, all the other fields are optional.
        </span>
        <v-spacer></v-spacer>

        <v-btn
          v-if="modalStore.newDataLogModalVerb === 'delete'"
          color="green darken-1"
          text
          @click="close">
          Close
        </v-btn>
        <v-btn
          v-else
          color="red darken-1"
          text
          @click="close">
          Close
        </v-btn>
        <v-btn
          v-if="modalStore.newDataLogModalVerb === 'delete'"
          color="red darken-1"
          text
          @click="submit"
        >
          Delete
        </v-btn>
        <v-btn
          v-else
          color="green darken-1"
          text
          @click="submit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { useModalStore } from '@/store';
import { usePopupStore } from '@/store';
import { ref, watchEffect, watch } from 'vue';

export default {
  name: 'NewDataLogModal',
  setup() {
    const modalStore = useModalStore();
    const popupStore = usePopupStore();

    const newDataLog = ref({
      aquarium_id: '',
      timestamp: '',
      temperature: null,
      ph: null,
      salinity: null,
      note: '',
      // --- new fields ---
      ammonia: null,
      nitrite: null,
      nitrate: null,
      phosphase: null,
      calcium: null,
      alkalinity: null,
      magnesium: null,
      // --- end new fields ---
    });

    const formatTimestamp = (ts) => {
      if (!ts) return '';
      const date = new Date(ts);
      if (isNaN(date)) return ts;
      const pad = n => n.toString().padStart(2, '0');
      let hours = date.getHours();
      const minutes = pad(date.getMinutes());
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      return `${year}-${month}-${day} ${pad(hours)}:${minutes} ${ampm}`;
    };

    // Initialize newDataLog when the modal becomes visible
    watchEffect(() => {
      if (modalStore.isNewDataLogModalVisible) {
        const prefilledData = { ...modalStore.prefilledDataLogData };
        if (prefilledData.timestamp) {
          prefilledData.timestamp = formatTimestamp(prefilledData.timestamp);
        }
        newDataLog.value = {
          aquarium_id: '',
          timestamp: '',
          temperature: null,
          ph: null,
          salinity: null,
          note: '',
          ammonia: null,
          nitrite: null,
          nitrate: null,
          phosphase: null,
          calcium: null,
          alkalinity: null,
          magnesium: null,
          ...prefilledData,
        };
      }
    });

    return {
      debugMode: false,
      modalStore,
      popupStore,
      newDataLog,
    };
  },
  methods: {
    close() {
      this.modalStore.closeNewDataLogModal();
    },
    async submit() {
      const payload = { ...this.newDataLog };
      ['temperature', 'ph', 'salinity', 'ammonia', 'nitrite', 'nitrate', 'phosphase', 'calcium', 'alkalinity', 'magnesium'].forEach(key => {
        if (payload[key] !== null && payload[key] !== '') {
          payload[key] = Number(payload[key]);
        }
      });
      if (this.modalStore.newDataLogModalVerb === 'create') {
        try {
          const response = await axios.post('/api/logs/', payload);
          this.popupStore.setMessage('Data Log Created', 'Log successfully added!', 'success');
          this.$eventBus.emit('log-updated');
          this.close();
        } catch (error) {
          this.popupStore.setMessage('Error Creating Data Log', error.response?.data?.error || error.response?.data?.message || error.message);
        }
      } else if (this.modalStore.newDataLogModalVerb === 'edit') {
        try {
          const response = await axios.put(`/api/logs/${this.newDataLog.id}`, payload);
          this.popupStore.setMessage('Data Log Edited', 'Log successfully updated!', 'success');
          this.$eventBus.emit('log-updated');
          this.close();
        } catch (error) {
          this.popupStore.setMessage('Error Updating Data Log', error.response?.data || error.response?.data?.message || error.message);
        }
      }
      else if (this.modalStore.newDataLogModalVerb === 'delete') {
        try {
          await axios.delete(`/api/logs/${this.newDataLog.id}`);
          this.popupStore.setMessage('Data Log Deleted', 'Log successfully deleted!', 'success');
          this.$eventBus.emit('log-updated');
          this.close();
        } catch (error) {
          this.popupStore.setMessage('Error Deleting Data Log', error.response?.data?.error || error.response?.data?.message || error.message);
        }
        return;
      }
    },
  },
};
</script>

<style scoped>
.text-subtle {
  color: #888;
  font-size: 0.85em;
}
</style>